import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { X, FileText, Pill, TestTube, Activity, Calendar, User, Loader2 } from 'lucide-react';
import { Badge } from '../components/ui/badge';
import { formatDateTimeIST } from '../utils/date';
import { formatDoctorName } from '../utils/doctor';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const NARRATIVE_SECTIONS = [
  { key: 'chief_complaints', label: 'Chief Complaints' },
  { key: 'past_history', label: 'Past History' },
  { key: 'tumor_board', label: 'Tumor Board' },
  { key: 'clinical_examination', label: 'Clinical Examination' },
  { key: 'provisional_diagnosis', label: 'Provisional Diagnosis' },
  { key: 'final_diagnosis', label: 'Final Diagnosis' },
  { key: 'treatment_plan', label: 'Treatment Plan' },
  { key: 'visit_notes', label: 'Visit Notes' },
];

function isMeaningful(html) {
  if (!html || typeof html !== 'string') return false;
  const text = html.replace(/<[^>]*>/g, '').trim();
  return text.length > 0;
}

export function PastVisitDrawer({ consultationId, onClose, title }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!consultationId) return;
    let cancelled = false;
    setLoading(true);
    axios.get(`${API_URL}/api/consultations/${consultationId}`, { withCredentials: true })
      .then(({ data }) => { if (!cancelled) { setData(data); setLoading(false); } })
      .catch((e) => { if (!cancelled) { setError(e?.response?.data?.detail || 'Failed to load visit'); setLoading(false); } });
    return () => { cancelled = true; };
  }, [consultationId]);

  const sections = data?.sections || {};
  const vitalsRaw = sections.general_info?.content || sections.general_info || {};
  const vitalsData = typeof vitalsRaw === 'object' ? vitalsRaw : {};
  const vitals = [
    ['BP', vitalsData.bp],
    ['Pulse', vitalsData.pulse && `${vitalsData.pulse} bpm`],
    ['Temp', vitalsData.temperature && `${vitalsData.temperature}°F`],
    ['SpO₂', vitalsData.spo2 && `${vitalsData.spo2}%`],
    ['Resp', vitalsData.respiration_rate && `${vitalsData.respiration_rate}/min`],
    ['Weight', vitalsData.weight && `${vitalsData.weight} kg`],
    ['Height', vitalsData.height && `${vitalsData.height} cm`],
    ['BMI', vitalsData.bmi],
  ].filter(([, v]) => v);

  const meds = sections.prescription?.items || [];
  // Defensive: legacy docs may carry investigation items as objects
  // `{name: 'CBC'}` (regression from early full-visit voice flow).
  // Rendering an object as a React child throws #31 and blanks the
  // drawer — coerce to plain strings here.
  const invs = (sections.investigations?.items || [])
    .map((t) => (typeof t === 'string' ? t : ((t && (t.name || t.test_name)) || '')))
    .filter(Boolean);
  // Review date is stored as a plain ISO date string under .date, not html.
  const reviewDate = (sections.review_date && (sections.review_date.date || sections.review_date.content)) || '';
  // Admission request is a structured object stored under `.data` (or `.content`
  // for legacy docs). Fall back gracefully when the section was never started.
  let admission = null;
  const adm = sections.admission_request;
  if (adm && typeof adm === 'object') {
    const candidate = adm.data ?? adm.content;
    if (candidate && typeof candidate === 'object' && !Array.isArray(candidate)) {
      admission = candidate;
    }
  }
  const admissionEntries = admission ? Object.entries(admission).filter(([, v]) => v !== '' && v !== null && v !== undefined) : [];

  return (
    <div className="fixed inset-0 z-50 flex justify-end" data-testid="past-visit-drawer">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative w-full md:w-[60%] bg-white shadow-2xl flex flex-col animate-in slide-in-from-right">
        <div className="px-5 py-4 border-b border-gray-200 bg-gradient-to-r from-blue-50 to-white flex items-center justify-between flex-shrink-0">
          <div className="flex items-center gap-2 min-w-0">
            <FileText className="h-5 w-5 text-blue-700 flex-shrink-0" />
            <div className="min-w-0">
              <h2 className="text-base font-bold text-gray-900 truncate" style={{ fontFamily: 'Manrope' }}>
                {title || 'Past Visit Details'}
              </h2>
              {data && (
                <p className="text-xs text-gray-500 mt-0.5 flex items-center gap-3 flex-wrap">
                  <span className="inline-flex items-center gap-1"><Calendar className="h-3 w-3" />{formatDateTimeIST(data.created_at)}</span>
                  <span className="inline-flex items-center gap-1"><User className="h-3 w-3" />{formatDoctorName(data.doctor_name)}</span>
                  {data.appointment_id && <span className="text-gray-400">{data.appointment_id}</span>}
                </p>
              )}
            </div>
          </div>
          <button onClick={onClose} className="p-1 rounded hover:bg-gray-100 flex-shrink-0" data-testid="past-visit-close">
            <X className="h-5 w-5 text-gray-500" />
          </button>
        </div>

        <div className="flex-1 overflow-auto px-5 py-4">
          {loading && (
            <div className="flex items-center justify-center py-16 text-gray-400">
              <Loader2 className="h-6 w-6 animate-spin mr-2" /> Loading visit details...
            </div>
          )}
          {error && !loading && (
            <div className="text-center py-16 text-red-500 text-sm">{error}</div>
          )}
          {data && !loading && (
            <div className="space-y-6">
              {vitals.length > 0 && (
                <Section icon={<Activity className="h-3.5 w-3.5 text-emerald-600" />} title="Vitals">
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                    {vitals.map(([label, value]) => (
                      <div key={label} className="bg-emerald-50/60 border border-emerald-100 rounded-lg px-3 py-2">
                        <div className="text-[10px] font-bold uppercase tracking-wide text-emerald-700">{label}</div>
                        <div className="text-sm font-semibold text-gray-900 mt-0.5">{value}</div>
                      </div>
                    ))}
                  </div>
                </Section>
              )}

              {NARRATIVE_SECTIONS.map(({ key, label }) => {
                const sec = sections[key];
                const html = (sec && typeof sec === 'object') ? sec.content : sec;
                if (!isMeaningful(html)) return null;
                return (
                  <Section key={key} title={label}>
                    <div className="text-sm text-gray-700 leading-relaxed prose prose-sm max-w-none" dangerouslySetInnerHTML={{ __html: html }} />
                  </Section>
                );
              })}

              {invs.length > 0 && (
                <Section
                  icon={<TestTube className="h-3.5 w-3.5 text-blue-600" />}
                  title="Investigations Ordered"
                  badge={`${invs.length} ${invs.length === 1 ? 'test' : 'tests'}`}
                >
                  <div className="flex flex-wrap gap-2">
                    {invs.map((inv, i) => {
                      const name = typeof inv === 'string' ? inv : (inv?.name || inv?.test_name || '');
                      if (!name) return null;
                      return (
                        <span key={i} className="inline-flex items-center gap-1.5 px-3 py-1 bg-gradient-to-br from-blue-50 to-sky-50 border border-blue-200 rounded-full text-xs font-semibold text-blue-900">
                          <span className="w-1.5 h-1.5 rounded-full bg-blue-600" />{name}
                        </span>
                      );
                    })}
                  </div>
                </Section>
              )}

              {meds.length > 0 && (
                <Section
                  icon={<Pill className="h-3.5 w-3.5 text-amber-600" />}
                  title="Prescription"
                  badge={`${meds.length} ${meds.length === 1 ? 'med' : 'meds'}`}
                >
                  <div className="overflow-x-auto -mx-1">
                    <table className="w-full text-xs border-collapse">
                      <thead>
                        <tr className="text-left text-[10px] font-bold uppercase tracking-wide text-gray-500">
                          <th className="px-2 py-2 border-b">#</th>
                          <th className="px-2 py-2 border-b">Medicine</th>
                          <th className="px-2 py-2 border-b">Dosage</th>
                          <th className="px-2 py-2 border-b">Freq</th>
                          <th className="px-2 py-2 border-b">Intake</th>
                          <th className="px-2 py-2 border-b">Timing</th>
                          <th className="px-2 py-2 border-b">Duration</th>
                          <th className="px-2 py-2 border-b">Qty</th>
                        </tr>
                      </thead>
                      <tbody>
                        {meds.map((m, i) => {
                          const t = m.timing || [];
                          const doses = m.timing_doses || {};
                          const timingStr = ['M', 'A', 'E', 'N'].map(c => {
                            const d = doses[c];
                            if (d === 0.5) return '½';
                            if (d === 1) return '1';
                            return t.includes(c) ? '1' : '0';
                          }).join('-');
                          const intakeMap = { before_food: 'Before Food', after_food: 'After Food', with_food: 'With Food', empty_stomach: 'Empty Stomach', any_time: 'Any Time' };
                          const intakeLabel = intakeMap[m.intake] || (m.intake ? String(m.intake).replace(/_/g, ' ') : '-');
                          const hasRemarks = m.remarks && String(m.remarks).trim();
                          return (
                            <React.Fragment key={i}>
                              <tr className="hover:bg-gray-50">
                                <td className="px-2 py-2 align-top text-gray-500">{i + 1}</td>
                                <td className="px-2 py-2 align-top font-semibold text-gray-900">{m.medicine_name || m.name}</td>
                                <td className="px-2 py-2 align-top">{m.dosage || '-'}</td>
                                <td className="px-2 py-2 align-top">{m.frequency || '-'}</td>
                                <td className="px-2 py-2 align-top">{intakeLabel}</td>
                                <td className="px-2 py-2 align-top font-mono tracking-wider text-gray-600">{timingStr}</td>
                                <td className="px-2 py-2 align-top">{m.duration || '-'}</td>
                                <td className="px-2 py-2 align-top">{m.quantity || '-'}</td>
                              </tr>
                              {hasRemarks && (
                                <tr>
                                  <td></td>
                                  <td colSpan={7} className="px-2 pb-2">
                                    <div className="inline-flex items-start gap-2 px-2.5 py-1 bg-amber-50 border-l-2 border-amber-500 rounded-sm">
                                      <span className="text-[9px] font-bold uppercase tracking-wide text-amber-700 mt-0.5">Remarks</span>
                                      <span className="text-[11px] text-amber-900">{m.remarks}</span>
                                    </div>
                                  </td>
                                </tr>
                              )}
                            </React.Fragment>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </Section>
              )}

              {admissionEntries.length > 0 && (
                <Section title="Admission Request">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {admissionEntries.map(([k, v]) => (
                      <div key={k} className="bg-gray-50 border border-gray-200 rounded-md px-3 py-2">
                        <div className="text-[10px] font-bold uppercase tracking-wide text-gray-500">{k.replace(/_/g, ' ')}</div>
                        <div className="text-sm text-gray-900 mt-0.5 break-words">{typeof v === 'object' ? JSON.stringify(v) : String(v)}</div>
                      </div>
                    ))}
                  </div>
                </Section>
              )}

              {reviewDate && (
                <Section icon={<Calendar className="h-3.5 w-3.5 text-violet-600" />} title="Next Review Date">
                  <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-violet-50 border border-violet-200 rounded-md text-sm font-semibold text-violet-900">
                    {formatDateTimeIST(reviewDate) || reviewDate}
                  </div>
                </Section>
              )}

              {meds.length === 0 && invs.length === 0 && vitals.length === 0 && admissionEntries.length === 0 && !reviewDate && !NARRATIVE_SECTIONS.some(s => isMeaningful(sections[s.key]?.content || sections[s.key])) && (
                <div className="text-center py-12 text-gray-400 text-sm">No clinical data captured for this visit.</div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function Section({ title, icon, badge, children }) {
  return (
    <div>
      <div className="flex items-center justify-between border-b border-gray-200 pb-1.5 mb-3">
        <h3 className="text-[11px] font-bold uppercase tracking-wider text-gray-700 flex items-center gap-1.5">
          {icon}{title}
        </h3>
        {badge && <Badge className="bg-gray-100 text-gray-700 text-[10px] border-0">{badge}</Badge>}
      </div>
      {children}
    </div>
  );
}
